import html2canvas from 'html2canvas';
import * as jsPDF from 'jspdf';

// Single shared engine behind every Print / Download / Add to Chart button in the app (Super Bill, invoices,
// intake form reports, vitals, enotes, rich text templates, health calendar & review reports).
// Replaces the old per-component copies that stamped one full-height image at a shrinking negative offset on
// each page: with no page margins and no awareness of where a table row / paragraph / image sat, that approach
// cut straight through content at the 297mm boundary (see the Super Bill in tttt.pdf).
export const PDF_PAGE_WIDTH_MM = 210;
export const PDF_PAGE_HEIGHT_MM = 297;
export const PDF_MARGIN_MM = 10;
export const PDF_CONTENT_HEIGHT_MM = PDF_PAGE_HEIGHT_MM - 2 * PDF_MARGIN_MM;

// Elements a page break should never land inside. Callers can extend this with their own selector
// (e.g. a signature block) via the `avoidBreakSelector` option; `.avoid-break` is always honoured.
export const DEFAULT_AVOID_BREAK_SELECTOR = '.avoid-break, tr, li, img, h1, h2, h3, h4, h5, h6';

// Class TinyMCE's `pagebreak` plugin content is turned into before the DOM is rasterized, so an author-placed
// break is always honoured as a hard page boundary rather than just another candidate cut point.
export const PAGE_BREAK_CLASS = 'rt-page-break';
const PAGE_BREAK_COMMENT = /<!--\s*pagebreak\s*-->/gi;

export function pageBreaksToMarkers(html: string, visible: boolean): string {
  if (!html) {
    return html;
  }
  const style = visible ? 'border-top:1px dashed #9aa5b1;margin:18px 0;height:0' : 'height:0;margin:0';
  return html.replace(PAGE_BREAK_COMMENT, `<div class="${PAGE_BREAK_CLASS}" style="${style}"></div>`);
}

export interface PdfPaginationOptions {
  avoidBreakSelector?: string;
}

export function renderElementToPdf(element: HTMLElement, options: PdfPaginationOptions = {}): Promise<any> {
  // html2canvas blocks the main thread almost immediately, before the browser gets a chance to paint
  // whatever loading indicator the caller just showed. Yielding one tick first lets that paint happen,
  // so the click feels acknowledged instead of looking like nothing happened.
  return new Promise(resolve => setTimeout(resolve, 0))
    .then(() => html2canvas(element, {width: element.offsetWidth, height: element.offsetHeight}))
    .then(canvas => elementCanvasToPdf(canvas, element, options));
}

// Exported separately so callers that already have a canvas (e.g. a caller batching several screenshots) don't
// have to re-run html2canvas.
export function elementCanvasToPdf(canvas: HTMLCanvasElement, element: HTMLElement, options: PdfPaginationOptions = {}): any {
  const scale = canvas.width / element.offsetWidth;
  const mmPerCanvasPx = PDF_PAGE_WIDTH_MM / canvas.width;
  const pageBodyPx = Math.max(1, Math.floor(PDF_CONTENT_HEIGHT_MM / mmPerCanvasPx));

  const containerTop = element.getBoundingClientRect().top;
  const toCanvasY = (viewportY: number) => Math.round((viewportY - containerTop) * scale);

  const forcedBreaks = Array.from(element.querySelectorAll('.' + PAGE_BREAK_CLASS))
    .map(node => toCanvasY(node.getBoundingClientRect().top))
    .filter(y => y > 0 && y < canvas.height)
    .sort((a, b) => a - b);

  const selector = options.avoidBreakSelector || DEFAULT_AVOID_BREAK_SELECTOR;
  const unsafeRanges = Array.from(element.querySelectorAll(selector))
    .map(node => node.getBoundingClientRect())
    .filter(rect => rect.height > 0)
    .map(rect => ({start: toCanvasY(rect.top), end: toCanvasY(rect.bottom)}))
    .filter(range => range.end > 0 && range.start < canvas.height)
    .sort((a, b) => a.start - b.start);

  const doc = new jsPDF('p', 'mm');
  let firstPage = true;
  const addSlice = (y: number, height: number) => {
    if (height < 2) {
      return;
    }
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = height;
    slice.getContext('2d').drawImage(canvas, 0, y, canvas.width, height, 0, 0, canvas.width, height);
    if (!firstPage) {
      doc.addPage();
    }
    firstPage = false;
    // jsPDF embeds PNG data as a raw, uncompressed bitmap unless told otherwise: a document with real
    // content easily balloons past 20MB (verified: an 8-page text form came to 27.6MB raw vs 1.5MB with
    // this on), which is most of why generating and uploading a PDF has felt so slow.
    doc.addImage(slice.toDataURL('image/png'), 'PNG', 0, PDF_MARGIN_MM, PDF_PAGE_WIDTH_MM, height * mmPerCanvasPx, undefined, 'FAST');
  };

  const segmentBounds = [0, ...forcedBreaks, canvas.height];
  for (let s = 0; s < segmentBounds.length - 1; s++) {
    let y = segmentBounds[s];
    const segmentEnd = segmentBounds[s + 1];
    let guard = 0;
    while (y < segmentEnd && guard++ < 1000) {
      let sliceEnd = Math.min(y + pageBodyPx, segmentEnd);
      if (sliceEnd < segmentEnd) {
        // Don't cut through a row/paragraph/image/heading: if the natural boundary falls inside one, push the
        // whole element onto the next page instead. A single element taller than one page is cut as a last resort.
        const straddling = unsafeRanges.find(range => range.start < sliceEnd && range.end > sliceEnd && range.start > y);
        if (straddling) {
          sliceEnd = straddling.start;
        }
      }
      addSlice(y, sliceEnd - y);
      y = sliceEnd;
    }
  }

  if (firstPage) {
    // Degenerate case (zero-height element): still emit a usable single-page document.
    addSlice(0, canvas.height);
  }
  return doc;
}

export function printPdfDoc(doc: any): void {
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
}

export function savePdfDoc(doc: any, fileName = 'file.pdf'): void {
  doc.save(fileName);
}
